import { useState, useEffect } from 'react'
import { Handle, Position } from 'reactflow'
import { cn } from '@/lib/utils'
import { FileText, Loader2, Settings2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { analyzeImage as analyzeImageService } from '@/services/image-services'

interface DescriberNodeData {
  id: string
  imageB64?: string
  description?: string
  userMessage?: string
  onDescriptionGenerated?: (description: string) => void
}

const DEFAULT_USER_MESSAGE = 'What is in this image? Provide a detailed description.'

export function DescriberNode({ id, data }: { id: string; data: DescriberNodeData }) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [description, setDescription] = useState<string>(data.description || '')
  const [isEditing, setIsEditing] = useState(!data.description)
  const [userMessage, setUserMessage] = useState(data.userMessage || DEFAULT_USER_MESSAGE)

  const isActionPhase = !data.imageB64 || isLoading
  const hasError = !!error

  useEffect(() => {
    if (description) {
      // Update both the callback and the node data
      data.onDescriptionGenerated?.(description)
      data.description = description // Store description in node data
    }
  }, [description])

  useEffect(() => {
    console.log('DescriberNode data:', data)
    if (data.imageB64 && !isLoading) {
      runImageAnalysis(data.imageB64)
    }
  }, [data.imageB64])

  const runImageAnalysis = async (imageB64: string) => {
    console.log('Starting image analysis')
    setIsLoading(true)
    setError(null)

    try {
      const result = await analyzeImageService(imageB64, {
        prompt: userMessage
      });

      if (result.error) {
        throw new Error(result.error);
      }

      const newDescription = result.description;
      setDescription(newDescription);
      setIsEditing(false);
      
      // Call the callback to trigger the photo node
      console.log('Triggering photo node with description:', newDescription);
      if (data.onDescriptionGenerated) {
        data.onDescriptionGenerated(newDescription);
      }
      // Also update the node data
      data.description = newDescription;
    } catch (err) {
      console.error('Error analyzing image:', err)
      setError(err instanceof Error ? err.message : 'Failed to analyze image')
    } finally {
      setIsLoading(false)
    }
  }

  const handleReanalyze = () => {
    if (data.imageB64) {
      runImageAnalysis(data.imageB64)
    }
  }

  return (
    <div
      className={cn(
        'bg-card border rounded-lg shadow-lg w-[300px] transition-all',
        isActionPhase && 'opacity-50',
        hasError && 'border-red-500'
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ width: '10px', height: '10px', background: '#555' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ width: '10px', height: '10px', background: '#555' }}
      />

      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Image Description</span>
            <Badge variant={isActionPhase ? "outline" : "secondary"} className="h-5">
              {isActionPhase ? "Waiting" : "Ready"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {!isActionPhase && !isEditing && (
              <Button 
                variant="ghost" 
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleReanalyze}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            {isEditing && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Analysis Prompt
                      </label>
                      <Textarea
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        placeholder="Enter prompt for image analysis..."
                        className="min-h-[100px] text-sm"
                      />
                    </div>
                    <Button 
                      size="sm" 
                      onClick={handleReanalyze}
                      disabled={!data.imageB64 || isLoading}
                    >
                      Reanalyze Image
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {!isActionPhase && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Done' : 'Edit'}
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !data.imageB64 ? (
          <div className="flex justify-center items-center p-8 border-2 border-dashed rounded-lg">
            <p className="text-sm text-muted-foreground">Connect to an image source</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Description */}
            {isEditing ? (
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description..."
                className="min-h-[100px] text-sm"
              />
            ) : description ? (
              <p className="text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-xs text-red-500">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
